export type OpeningContextMacro = {
  openingProblemId: number;
  moves: string[];
};

export const OPENING_VIEWER_MOVES_PARAM = "moves";

export type OpeningViewerMovesParam =
  | { kind: "absent" }
  | { kind: "malformed" }
  | { kind: "valid"; moves: string[] };

export type OpeningSequenceMatch<TNode> = {
  node: TNode | null;
  matchedMoves: string[];
  exact: boolean;
};

const PGN_RESULTS = new Set(["*", "1-0", "0-1", "1/2-1/2"]);

export function parseOpeningMoveSequence(raw: string): string[] {
  return raw
    .replace(/\{[^}]*\}/g, " ")
    .replace(/\([^)]*\)/g, " ")
    .replace(/\d+\.(?:\.\.)?/g, " ")
    .split(/[,\s]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .filter((token) => !PGN_RESULTS.has(token))
    .filter((token) => !token.startsWith("$"));
}

export function parseOpeningMacroValue(
  rawValue: string,
): OpeningContextMacro | null {
  const match = /^([1-9]\d*)(?:\s+"([^"]*)")?\s*$/.exec(rawValue.trim());
  if (!match) return null;

  return {
    openingProblemId: Number(match[1]),
    moves: parseOpeningMoveSequence(match[2] ?? ""),
  };
}

export function serializeStoredOpeningMoves(moves: readonly string[]): string {
  return JSON.stringify(moves);
}

export function parseStoredOpeningMoves(
  value: string | null | undefined,
): string[] {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value) as unknown;
    if (
      !Array.isArray(parsed) ||
      !parsed.every((move) => typeof move === "string")
    ) {
      return [];
    }
    return parsed;
  } catch {
    return [];
  }
}

export function encodeOpeningViewerMovesParam(moves: string[]): string {
  return serializeStoredOpeningMoves(moves);
}

export function decodeOpeningViewerMovesParam(
  rawValue: string | null | undefined,
): OpeningViewerMovesParam {
  if (rawValue == null) return { kind: "absent" };

  try {
    const parsed = JSON.parse(rawValue) as unknown;
    if (
      !Array.isArray(parsed) ||
      !parsed.every((move) => typeof move === "string")
    ) {
      return { kind: "malformed" };
    }
    return { kind: "valid", moves: parsed };
  } catch {
    return { kind: "malformed" };
  }
}

export function formatOpeningMacro(context: OpeningContextMacro): string {
  if (context.moves.length === 0) {
    return `[Opening ${context.openingProblemId}]`;
  }

  return `[Opening ${context.openingProblemId} "${context.moves.join(" ")}"]`;
}

export function findMoveSequenceMatch<
  TNode extends { notation: string; children?: readonly TNode[] },
>(
  root: TNode,
  moves: readonly string[],
  moveMatcher: (requestedMove: string, nodeMove: string) => boolean = (
    requestedMove,
    nodeMove,
  ) => requestedMove.trim() === nodeMove.trim(),
): OpeningSequenceMatch<TNode> {
  let current = root;
  const matchedMoves: string[] = [];

  for (const move of moves) {
    const next = current.children?.find((child) =>
      moveMatcher(move, child.notation),
    );
    if (!next) {
      return {
        node: current === root ? null : current,
        matchedMoves,
        exact: false,
      };
    }

    current = next;
    matchedMoves.push(move);
  }

  return {
    node: moves.length === 0 ? null : current,
    matchedMoves,
    exact: true,
  };
}
